import * as React from "react";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { graphql } from "gatsby";
import { when } from "../../styles/theme";
import theme from "../../styles/theme";
import { SectionTitle } from "../typography";
import { useLocalization } from "gatsby-theme-i18n";
import pageBackground from "../../images/page_background_blue.jpg";

export const fragment = graphql`
  fragment IntroductionContent on ContentfulPageSectionIntroduction {
    name
    title
    description {
      childMarkdownRemark {
        html
      }
    }
    introductionImage {
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
      description
    }
    description2 {
      childMarkdownRemark {
        html
      }
    }
    introductionImage2 {
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
      description
    }

    internal {
      type
    }
  }
`;

const Container = styled.div`
  padding: 10rem 0;
  background-color: ${(props) => props.theme.colors.darkBlue};
  color: ${(props) => props.theme.colors.white};
  width: 100%;
  overflow: hidden;
  position: relative;
`;
const ContainerBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  //z-index: -1;
  background-image: url(${pageBackground});
  background-repeat: repeat;
  background-position: center bottom;
`;

const ContentSection = styled(PageContainer)`
  margin-top: 5rem;
  position: relative;
  z-index: 1;

  &:first-child {
    margin-top: 0;
  }

  ${when("md")} {
    display: flex;
    align-items: ${(props) => (props.alignCenter ? "center" : "flex-start")};
    flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};
    column-gap: ${(props) => props.theme.gutterWidth.md}rem;
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.reverse ? "flex-end" : "flex-start")};

  ${when("md")} {
    flex: 1;
    align-self: flex-start;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 100rem;
  width: 25rem;
  margin-right: 2rem;
  //margin-bottom: 0.5rem;
  max-width: 45rem;

  ${when("md")} {
    width: 80%;
  }
`;

const Circle = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  ${(props) => (props.reverse ? "right" : "left")}: -50%;
  background-color: ${(props) => props.theme.colors.darkBlue};
  border-radius: 9999rem;
`;

const Image = styled(GatsbyImage)`
  border-radius: 50%;
  overflow: hidden;
  transform: translateZ(0);
`;

const Content = styled.div`
  ${when("md")} {
    flex: 1;
  }
`;

const RedDot = styled.div`
  width: 7rem;
  height: 7rem;
  background-color: ${(props) => props.theme.colors.red};
  border-radius: 1000px;

  ${when("md")} {
    margin-left: -3.5rem;
  }
`;

const Title = styled(SectionTitle)`
  position: absolute;
  bottom: 0;
  left: 3.5rem;
  font-size: 3.6rem;
  line-height: 5.2rem;
  letter-spacing: ${(props) => (props.isEn ? "0" : "0.22em")};

  ${when("md")} {
    left: 0;
  }
`;

const TitleContainer = styled.div`
  margin-top: 4rem;
  position: relative;
`;

const DescriptionContainer = styled.div`
  margin-top: 3rem;
  max-width: 60rem;
  margin-left: ${(props) => (props.reverse ? "auto" : "0")};

  ${when("md")} {
    margin-top: ${(props) => (props.reverse ? "0" : "3")}rem;
  }
`;

const Introduction = ({ data }) => {
  const { locale } = useLocalization();

  const {
    title,
    description,
    introductionImage,
    description2,
    introductionImage2,
  } = data;
  return (
    <Container as={"section"}>
      <ContainerBackground />
      <ContentSection>
        <ImageContainer>
          <ImageWrapper>
            <Circle>
              <StaticImage
                src="../../images/background_pattern.png"
                alt=""
                width={450}
                height={450}
                placeholder={"none"}
              />
            </Circle>
            <Image
              image={introductionImage?.gatsbyImageData}
              alt={introductionImage?.description}
            />
          </ImageWrapper>
        </ImageContainer>
        <Content>
          <TitleContainer>
            <RedDot />
            <Title color={theme.colors.white} isEn={locale === "en"} as={"h1"}>
              {title}
            </Title>
          </TitleContainer>
          <DescriptionContainer
            dangerouslySetInnerHTML={{
              __html: description?.childMarkdownRemark.html,
            }}
          />
        </Content>
      </ContentSection>
      <ContentSection reverse={true} alignCenter={true}>
        <ImageContainer reverse={true}>
          <ImageWrapper>
            <Circle reverse={true}>
              <StaticImage
                src="../../images/background_pattern.png"
                alt=""
                width={450}
                height={450}
                placeholder={"none"}
              />
            </Circle>
            <Image
              image={introductionImage2?.gatsbyImageData}
              alt={introductionImage2?.description}
            />
          </ImageWrapper>
        </ImageContainer>
        <Content>
          <DescriptionContainer
            reverse={true}
            dangerouslySetInnerHTML={{
              __html: description2?.childMarkdownRemark.html,
            }}
          />
        </Content>
      </ContentSection>
    </Container>
  );
};

export const introductionName = "ContentfulPageSectionIntroduction";

export default Introduction;
